import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  VStack,
  Heading,
  Text,
  Checkbox,
  Select,
  Spinner,
  Container,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  FormControl,
  FormLabel,
  Input,
  HStack,
  Badge,
  Flex,
  Icon,
  useColorModeValue,
  useToast,
  Card,
  CardBody,
  CardHeader,
} from "@chakra-ui/react";
import { FiCalendar } from "react-icons/fi";
import { hitBeApi } from "../../../api/api";
import TabCacheButton from "../../common/TabCacheButton";
import {
  DashboardDataTable,
  useDashboardTableStyles,
} from "../utils/DashboardDataTable";

const defaultSettings = {
  reminderNotifyBy: "sms",
  reminderMinutesBefore: 3,
  notifyAllMeetings: true,
  reminderPhoneNumber: "",
};

const CalendarEvents = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const [events, setEvents] = useState([]);
  const [settings, setSettings] = useState(defaultSettings);
  const [selectedEvents, setSelectedEvents] = useState({});
  const [settingsDirty, setSettingsDirty] = useState(false);
  const toast = useToast();
  const tableStyles = useDashboardTableStyles();

  const pageBg = useColorModeValue("gray.50", "gray.900");
  const surfaceCard = useColorModeValue("white", "gray.800");
  const mutedLine = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.800", "gray.100");
  const subtextColor = useColorModeValue("gray.600", "gray.400");
  const inputBg = useColorModeValue("gray.50", "gray.900");
  const formBorder = useColorModeValue("gray.200", "gray.600");
  const placeholderColor = useColorModeValue("gray.500", "gray.500");
  const connectShellBg = useColorModeValue("blue.50", "rgba(59, 130, 246, 0.12)");
  const statusStripBg = useColorModeValue("green.50", "rgba(34, 197, 94, 0.12)");
  const settingsHeaderBg = useColorModeValue("gray.50", "gray.700");

  const fetchEvents = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await hitBeApi(
        `calendar-events?t=${Date.now()}`,
        "GET"
      );
      const payload = response.data || {};
      setConnected(!!payload.connected);
      const evs = Array.isArray(payload.events) ? payload.events : [];
      setEvents(evs);
      setSettings({ ...defaultSettings, ...(payload.settings || {}) });
      const initialSel = {};
      evs.forEach((e) => {
        if (e.reminderEnabled) {
          initialSel[e.eventId] = String(e.reminderTimeBefore ?? 3);
        }
      });
      setSelectedEvents(initialSel);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to load calendar.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected") === "1" || params.get("error") === "oauth") {
      fetchEvents();
      if (params.get("error") === "oauth") {
        toast({
          title: "Calendar connection failed",
          description: "Try again or check Google Cloud OAuth settings.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
      window.history.replaceState({}, "", "/dashboard/calendar-events");
    }
  }, [fetchEvents, toast]);

  const handleGrantAccess = async () => {
    setIsLoading(true);
    try {
      const response = await hitBeApi("calendar-events/auth", "GET");
      const authUrl = response.data?.authUrl;
      if (!authUrl) {
        throw new Error("No auth URL returned");
      }
      window.location.href = authUrl;
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to initiate calendar access.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      setIsLoading(false);
    }
  };

  const handleEventSelection = (eventId, reminderTime) => {
    setSelectedEvents((prev) => ({
      ...prev,
      [eventId]: reminderTime,
    }));
  };

  const saveSelectedEvents = async () => {
    try {
      await hitBeApi("calendar-events/select-events", "POST", {
        selectedEvents,
      });
      toast({
        title: "Saved",
        description: "Per-event reminder preferences updated.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      fetchEvents();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to save preferences.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const saveSettings = async () => {
    try {
      await hitBeApi("calendar-events/settings", "PUT", {
        reminderNotifyBy: settings.reminderNotifyBy,
        reminderMinutesBefore: settings.reminderMinutesBefore,
        notifyAllMeetings: settings.notifyAllMeetings,
        reminderPhoneNumber: settings.reminderPhoneNumber,
      });
      setSettingsDirty(false);
      toast({
        title: "Settings saved",
        description:
          "We will SMS or call this number a few minutes before each meeting (server checks every minute).",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      fetchEvents();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to save settings.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const rowReminderOn = (event) => {
    if (Object.prototype.hasOwnProperty.call(selectedEvents, event.eventId)) {
      return selectedEvents[event.eventId] != null;
    }
    return !!event.reminderEnabled;
  };

  const formatDateTime = (startTime, endTime) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const date = start.toLocaleDateString();
    const startTimeStr = start.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
    const endTimeStr = end.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${date}, ${startTimeStr} – ${endTimeStr}`;
  };

  return (
    <Box minH="100vh" bg={pageBg} py={{ base: 4, md: 8 }}>
      <Container maxW="container.xl" px={{ base: 3, md: 6 }}>
        <VStack spacing={{ base: 5, md: 7 }} align="stretch">
          <Box
            role="region"
            aria-label="Calendar meeting alerts"
            overflow="hidden"
            borderRadius="2xl"
            boxShadow="lg"
            borderWidth="1px"
            borderColor={mutedLine}
            bg={surfaceCard}
          >
            <Box
              w="100%"
              minH={{ base: "132px", md: "auto" }}
              px={{ base: 5, md: 8 }}
              py={{ base: 6, md: 7 }}
              bg="blue.600"
              backgroundImage="linear-gradient(90deg, #2563eb 0%, #4f46e5 55%, #4338ca 100%)"
            >
              <Flex
                align="flex-start"
                justify="space-between"
                gap={4}
                direction={{ base: "column", sm: "row" }}
              >
                <Flex
                  align="flex-start"
                  gap={4}
                  direction={{ base: "column", sm: "row" }}
                  flex="1"
                  minW={0}
                >
                  <Flex
                    align="center"
                    justify="center"
                    w={12}
                    h={12}
                    borderRadius="xl"
                    bg="whiteAlpha.300"
                    flexShrink={0}
                    borderWidth="1px"
                    borderColor="whiteAlpha.400"
                  >
                    <Icon as={FiCalendar} boxSize={7} color="white" aria-hidden />
                  </Flex>
                  <Box textAlign="left" flex="1" minW={0}>
                    <Heading
                      as="h1"
                      size="lg"
                      color="white"
                      fontWeight="extrabold"
                      letterSpacing="tight"
                    >
                      Calendar meeting alerts
                    </Heading>
                    <Text
                      fontSize="sm"
                      color="white"
                      opacity={0.95}
                      mt={2}
                      maxW="3xl"
                      lineHeight="tall"
                    >
                      Connect Google Calendar (read-only). The server checks every
                      minute and can SMS or call you shortly before each meeting.
                      Default lead time is 3 minutes — adjust after you connect.
                    </Text>
                  </Box>
                </Flex>
                <TabCacheButton tabId="calendar-events" onGradient />
              </Flex>
            </Box>
          </Box>

          {!connected ? (
            <Card
              borderRadius="xl"
              borderWidth="1px"
              borderColor={mutedLine}
              bg={surfaceCard}
              boxShadow="md"
              overflow="hidden"
            >
              <CardBody
                py={{ base: 10, md: 12 }}
                px={{ base: 4, md: 8 }}
                textAlign="center"
                bg={connectShellBg}
              >
                <Text fontWeight="semibold" color={textColor} fontSize="lg" mb={2}>
                  Connect to get started
                </Text>
                <Text fontSize="sm" color={subtextColor} mb={6} maxW="md" mx="auto">
                  Grant read access to your Google Calendar, then add your phone
                  number and choose SMS or voice — all from the next screen after
                  you authorize.
                </Text>
                <Button
                  colorScheme="blue"
                  size="lg"
                  px={8}
                  borderRadius="full"
                  boxShadow="md"
                  onClick={handleGrantAccess}
                  isLoading={isLoading}
                  loadingText="Connecting…"
                  _hover={{ transform: "translateY(-1px)", boxShadow: "lg" }}
                >
                  Connect Google Calendar
                </Button>
              </CardBody>
            </Card>
          ) : (
            <>
              <Card
                borderRadius="xl"
                borderWidth="1px"
                borderColor={mutedLine}
                bg={surfaceCard}
                boxShadow="sm"
                overflow="hidden"
              >
                <CardHeader
                  pb={2}
                  borderBottomWidth="1px"
                  borderColor={mutedLine}
                  bg={settingsHeaderBg}
                >
                  <Heading size="sm" color={textColor} fontWeight="bold">
                    How to reach you
                  </Heading>
                  <Text fontSize="xs" color={subtextColor} fontWeight="normal" mt={1}>
                    Phone and channel used for meeting reminders
                  </Text>
                </CardHeader>
                <CardBody>
                  <VStack spacing={4} align="stretch">
                    <FormControl>
                      <FormLabel fontSize="sm" color={textColor}>
                        Phone (E.164, e.g. +9198…)
                      </FormLabel>
                      <Input
                        size="md"
                        borderRadius="md"
                        borderColor={formBorder}
                        bg={inputBg}
                        _placeholder={{ color: placeholderColor }}
                        placeholder="+1…"
                        value={settings.reminderPhoneNumber || ""}
                        onChange={(e) => {
                          setSettings((s) => ({
                            ...s,
                            reminderPhoneNumber: e.target.value,
                          }));
                          setSettingsDirty(true);
                        }}
                      />
                      <Text fontSize="xs" color={subtextColor} mt={1}>
                        Uses your profile phone if left empty (when set on your
                        account).
                      </Text>
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm" color={textColor}>
                        Notify by
                      </FormLabel>
                      <Select
                        size="md"
                        borderRadius="md"
                        borderColor={formBorder}
                        bg={inputBg}
                        value={settings.reminderNotifyBy || "sms"}
                        onChange={(e) => {
                          setSettings((s) => ({
                            ...s,
                            reminderNotifyBy: e.target.value,
                          }));
                          setSettingsDirty(true);
                        }}
                      >
                        <option value="sms">SMS</option>
                        <option value="call">Phone call (voice)</option>
                        <option value="both">SMS + call</option>
                        <option value="whatsapp">WhatsApp</option>
                        <option value="sms_whatsapp">SMS + WhatsApp</option>
                      </Select>
                      <Text fontSize="xs" color={subtextColor} mt={1}>
                        WhatsApp needs the server WhatsApp integration enabled and a WhatsApp-supported number (with
                        country code). Use mirror flags in env to also send WhatsApp alongside SMS/call alerts.
                      </Text>
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm" color={textColor}>
                        Minutes before meeting (default 3)
                      </FormLabel>
                      <Select
                        size="md"
                        borderRadius="md"
                        borderColor={formBorder}
                        bg={inputBg}
                        value={String(settings.reminderMinutesBefore ?? 3)}
                        onChange={(e) => {
                          setSettings((s) => ({
                            ...s,
                            reminderMinutesBefore: Number(e.target.value),
                          }));
                          setSettingsDirty(true);
                        }}
                      >
                        <option value="3">3 minutes</option>
                        <option value="5">5 minutes</option>
                        <option value="10">10 minutes</option>
                        <option value="15">15 minutes</option>
                      </Select>
                    </FormControl>
                    <Checkbox
                      colorScheme="teal"
                      isChecked={settings.notifyAllMeetings !== false}
                      onChange={(e) => {
                        setSettings((s) => ({
                          ...s,
                          notifyAllMeetings: e.target.checked,
                        }));
                        setSettingsDirty(true);
                      }}
                    >
                      <Text as="span" fontSize="sm" color={textColor}>
                        Remind me for every upcoming meeting (recommended)
                      </Text>
                    </Checkbox>
                    <Button
                      colorScheme="teal"
                      size="md"
                      alignSelf={{ base: "stretch", sm: "flex-start" }}
                      borderRadius="full"
                      px={6}
                      onClick={saveSettings}
                      isDisabled={!settingsDirty}
                      boxShadow="sm"
                    >
                      Save alert settings
                    </Button>
                  </VStack>
                </CardBody>
              </Card>

              <Flex
                align={{ base: "stretch", sm: "center" }}
                justify="space-between"
                direction={{ base: "column", sm: "row" }}
                gap={3}
                px={4}
                py={3}
                borderRadius="xl"
                borderWidth="1px"
                borderColor={mutedLine}
                bg={statusStripBg}
                boxShadow="sm"
              >
                <HStack spacing={3} flexWrap="wrap">
                  <Badge colorScheme="green" fontSize="0.7rem" px={2} py={0.5} borderRadius="md">
                    Connected
                  </Badge>
                  <Text fontSize="sm" color={subtextColor}>
                    Upcoming events sync when you open this page.
                  </Text>
                </HStack>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="green"
                  borderRadius="full"
                  onClick={fetchEvents}
                >
                  Refresh
                </Button>
              </Flex>

              {isLoading && events.length === 0 ? (
                <Card borderRadius="xl" borderWidth="1px" borderColor={mutedLine} bg={surfaceCard}>
                  <CardBody py={16}>
                    <VStack spacing={4}>
                      <Spinner size="lg" color="blue.500" thickness="4px" />
                      <Text fontSize="sm" color={subtextColor}>
                        Loading your calendar…
                      </Text>
                    </VStack>
                  </CardBody>
                </Card>
              ) : (
                <VStack spacing={4} align="stretch">
                  <Box
                    px={1}
                    py={2}
                    borderLeftWidth="4px"
                    borderLeftColor="purple.400"
                    pl={4}
                    borderRadius="md"
                  >
                    <Text fontSize="md" fontWeight="semibold" color={textColor}>
                      {settings.notifyAllMeetings !== false
                        ? "All meetings below are eligible for alerts (unless you turn off the toggle above)."
                        : "Pick which meetings get reminders in the table."}
                    </Text>
                  </Box>
                  <DashboardDataTable
                    title="Your calendar events"
                    description={`${events.length} event${
                      events.length === 1 ? "" : "s"
                    } — optional per-event overrides when not using “every meeting”.`}
                    maxH={{ base: "none", md: "min(65vh, 520px)" }}
                    variant="emphasis"
                  >
                  <Table
                    variant="simple"
                    size="sm"
                    colorScheme="gray"
                    sx={{ borderCollapse: "separate", borderSpacing: 0 }}
                  >
                    <Thead
                      position="sticky"
                      top={0}
                      zIndex={1}
                      bg={tableStyles.tableHeaderBg}
                      backdropFilter="blur(8px)"
                      borderBottomWidth="1px"
                      borderColor={tableStyles.borderColor}
                    >
                      <Tr>
                        <Th {...tableStyles.thProps} w="48px">
                          On
                        </Th>
                        <Th {...tableStyles.thProps}>Event</Th>
                        <Th {...tableStyles.thProps}>When</Th>
                        <Th
                          {...tableStyles.thProps}
                          w={{ base: "160px", md: "180px" }}
                        >
                          Remind
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {events.length === 0 ? (
                        <Tr>
                          <Td colSpan={4} py={6} textAlign="center">
                            <Text color={subtextColor} fontSize="sm">
                              No upcoming events in the synced window.
                            </Text>
                          </Td>
                        </Tr>
                      ) : (
                        events.map((event) => (
                          <Tr key={event.eventId} sx={tableStyles.trHoverSx}>
                            <Td py={3} verticalAlign="top">
                              <Checkbox
                                colorScheme="teal"
                                isChecked={rowReminderOn(event)}
                                onChange={(e) =>
                                  handleEventSelection(
                                    event.eventId,
                                    e.target.checked
                                      ? String(
                                          event.reminderTimeBefore ?? 3
                                        )
                                      : null
                                  )
                                }
                              />
                            </Td>
                            <Td
                              py={3}
                              fontSize="sm"
                              fontWeight="semibold"
                              color={textColor}
                              maxW="280px"
                            >
                              {event.title}
                            </Td>
                            <Td
                              py={3}
                              fontSize="sm"
                              color={subtextColor}
                              whiteSpace="nowrap"
                            >
                              {formatDateTime(event.startTime, event.endTime)}
                            </Td>
                            <Td py={3} verticalAlign="top">
                              {rowReminderOn(event) ? (
                                <Select
                                  size="sm"
                                  maxW="180px"
                                  value={
                                    selectedEvents[event.eventId] != null
                                      ? String(selectedEvents[event.eventId])
                                      : String(event.reminderTimeBefore ?? 3)
                                  }
                                  onChange={(e) =>
                                    handleEventSelection(
                                      event.eventId,
                                      e.target.value
                                    )
                                  }
                                >
                                  <option value="3">3 mins before</option>
                                  <option value="5">5 mins before</option>
                                  <option value="10">10 mins before</option>
                                  <option value="15">15 mins before</option>
                                  <option value="30">30 mins before</option>
                                </Select>
                              ) : (
                                <Text fontSize="xs" color="gray.500">
                                  —
                                </Text>
                              )}
                            </Td>
                          </Tr>
                        ))
                      )}
                    </Tbody>
                  </Table>
                </DashboardDataTable>
                <Button
                  colorScheme="teal"
                  size="md"
                  borderRadius="full"
                  boxShadow="sm"
                  onClick={saveSelectedEvents}
                  isDisabled={Object.keys(selectedEvents).length === 0}
                >
                  Save per-event preferences
                </Button>
              </VStack>
            )}
          </>
        )}
      </VStack>
      </Container>
    </Box>
  );
};

export default CalendarEvents;
